import { useState } from 'react'
import {
  GetSocialAuthStartUrlMutationVariables,
  useGetSocialAuthStartUrlMutation,
} from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import { localStorageKeys } from '@sketch/constants'
import { setItem } from '@sketch/utils'
import * as Sentry from '@sentry/react'

interface DefaultLoadingState {
  isLoading: boolean
  provider: GetSocialAuthStartUrlMutationVariables['provider'] | null
}

const DEFAULT_LOADING_STATE: DefaultLoadingState = {
  isLoading: false,
  provider: null,
}

export const useGetSocialSignIn = () => {
  const { showToast } = useToast()
  const [isPersistentLoading, setIsPersistentLoading] = useState(
    DEFAULT_LOADING_STATE
  )

  const [getSocialAuthStartUrl] = useGetSocialAuthStartUrlMutation({
    onCompleted: data => {
      const { url, state } = data.socialAuthStartUrl

      /*
       * "state" is stored in local storage and later sent back
       * to the BE to trade for the proper JWT token.
       */
      state && setItem(localStorageKeys.socialState, state)

      /**
       * Safari opens a Apple Sign in Pop-up that does not trigger any event when the user clicks "Cancel"
       * So we need to do this workaround to be able to reset the loading state.
       * When the user clicks cancel the window is focused and we use that to reset the loading state
       */
      const handleFocus = () => {
        setIsPersistentLoading(DEFAULT_LOADING_STATE)
        window.removeEventListener('focus', handleFocus)
      }

      window.addEventListener('focus', handleFocus)

      window.location.href = url
    },
    onError: ({ message }) => {
      setIsPersistentLoading(DEFAULT_LOADING_STATE)
      showToast('Something went wrong', 'negative')
      Sentry.captureMessage(`Social Sign In Error: ${message}`)
    },
    fetchPolicy: 'network-only',
  })

  const handleGetSocialAuthStartUrl = (
    ...args: Parameters<typeof getSocialAuthStartUrl>
  ) => {
    const provider = args[0]?.variables?.provider || null

    setIsPersistentLoading({
      isLoading: true,
      provider,
    })
    return getSocialAuthStartUrl(...args)
  }

  return {
    getSocialAuthStartUrl: handleGetSocialAuthStartUrl,
    loadingState: isPersistentLoading,
  }
}
